/** A saved email (.eml) opened for the receipt viewer. */
export interface EmailView {
  subject: string;
  from: string;
  date: string | undefined;
  /** The email as a page, shown in a locked-down frame: no scripts, no forms, links open in a new tab. */
  page: string;
  files: { name: string; url: string }[];
}

const escape = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );

/** An attachment's bytes, copied into a plain ArrayBuffer, which is what a Blob takes. */
function bytesOf(content: ArrayBuffer | Uint8Array | string): Uint8Array<ArrayBuffer> {
  if (typeof content === "string") return new TextEncoder().encode(content);
  return content instanceof ArrayBuffer ? new Uint8Array(content) : Uint8Array.from(content);
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/**
 * Reads an .eml. Pictures the email shows by content id become data: addresses, as the frame
 * can't reach the app's own blob: ones. Other attachments are listed to download. The reader
 * (postal-mime) is loaded the first time an email is opened. Revoke the files' URLs when done.
 */
export async function readEmail(blob: Blob): Promise<EmailView> {
  const { default: PostalMime } = await import("postal-mime");
  const email = await PostalMime.parse(await blob.arrayBuffer());

  const inline = new Map<string, string>();
  const files: EmailView["files"] = [];
  for (const attachment of email.attachments) {
    const bytes = bytesOf(attachment.content);
    const id = attachment.contentId?.replace(/^<|>$/g, "");
    if (id && attachment.mimeType.startsWith("image/")) {
      inline.set(id, `data:${attachment.mimeType};base64,${base64(bytes)}`);
      if (attachment.related) continue;
    }
    files.push({
      name: attachment.filename ?? "attachment",
      url: URL.createObjectURL(new Blob([bytes], { type: attachment.mimeType })),
    });
  }

  const body = email.html
    ? email.html.replace(/cid:([^"'\s)>]+)/gi, (match, id: string) => inline.get(id) ?? match)
    : `<pre>${escape(email.text ?? "")}</pre>`;
  const page = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    body { margin: 16px; font: 15px/1.45 system-ui, -apple-system, "Segoe UI", sans-serif; color: #17292a; background: #fff; overflow-wrap: anywhere; }
    img { max-width: 100%; height: auto; }
    table { max-width: 100%; }
    pre { margin: 0; white-space: pre-wrap; font: inherit; }
  </style></head><body>${body}</body></html>`;

  const from = email.from
    ? "address" in email.from && email.from.address
      ? email.from.name
        ? `${email.from.name} <${email.from.address}>`
        : email.from.address
      : email.from.name
    : "";
  return { subject: email.subject ?? "(no subject)", from, date: email.date, page, files };
}
